// @ts-nocheck
import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import PersonIcon from '@mui/icons-material/Person';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { formatTimestamp } from '../../shared/utils/metadataUtils';
import { resetUserMfa, listTrustedDevices, revokeDevice, revokeAllDevices } from '../../services/adminService';


export default function UserViewDialog({ open, onClose, user, onUserUpdated, onRefreshUser }) {
  const [activeTab, setActiveTab] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState('');
  const [revokingDeviceId, setRevokingDeviceId] = useState(null);
  const [confirmingRevokeDeviceId, setConfirmingRevokeDeviceId] = useState(null);
  const [revokeAllConfirming, setRevokeAllConfirming] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setConfirming(false);
      setResetError('');
      setDevices([]);
      setDevicesError('');
      setConfirmingRevokeDeviceId(null);
      setRevokeAllConfirming(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || activeTab !== 1 || !user?.uid) return;
    setDevicesLoading(true);
    setDevicesError('');
    listTrustedDevices(user.uid)
      .then(({ devices: list }) => setDevices(list ?? []))
      .catch(() => setDevicesError('Failed to load trusted devices.'))
      .finally(() => setDevicesLoading(false));
  }, [open, activeTab, user?.uid]);

  if (!user) return null;

  const requiresMfa = user.mfaEnabled ?? false;
  const totpEnrolled = user.totpEnrolled ?? false;

  const handleResetMfa = async () => {
    setResetting(true);
    setResetError('');
    try {
      await resetUserMfa(user.uid);
      setConfirming(false);
      setDevices([]);
      onUserUpdated?.();
      onRefreshUser?.(user.uid);
    } catch {
      setResetError('Failed to reset MFA. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const handleRevokeDevice = async (deviceId) => {
    setRevokingDeviceId(deviceId);
    setDevicesError('');
    try {
      await revokeDevice(user.uid, deviceId);
      setDevices((prev) => prev.filter((d) => d.deviceId !== deviceId));
      setConfirmingRevokeDeviceId(null);
    } catch {
      setDevicesError('Failed to revoke device. Please try again.');
    } finally {
      setRevokingDeviceId(null);
    }
  };

  const handleRevokeAll = async () => {
    setRevokingAll(true);
    setDevicesError('');
    try {
      await revokeAllDevices(user.uid);
      setDevices([]);
      setRevokeAllConfirming(false);
    } catch {
      setDevicesError('Failed to revoke all devices. Please try again.');
    } finally {
      setRevokingAll(false);
    }
  };

  const hospitalValue = (user.hospitalName || '').trim();

  const disabledSx = {
    '& .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled': {
      color: 'rgba(0,0,0,0.87)',
    },
    '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.23)',
    },
    '& .MuiInputBase-root': {
      cursor: 'default',
    },
    '& .MuiInputBase-input': {
      cursor: 'default',
    },
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle>User details</DialogTitle>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Details" />
        <Tab label="MFA" />
      </Tabs>

      <DialogContent dividers>

        {/* ── Details Tab ─────────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}>
              <Avatar sx={{ width: 80, height: 80, fontSize: 32 }}>
                <PersonIcon fontSize="large" />
              </Avatar>
            </Box>

            <Typography variant="subtitle1" fontWeight="500" sx={{ mt: 1 }}>Identity</Typography>
            <TextField margin="normal" label="First name" value={user.firstName || ''} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Last name" value={user.lastName || ''} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Display name" value={user.displayName || ''} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Email" value={user.email || ''} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            {hospitalValue && (
              <TextField margin="normal" label="Hospital" value={hospitalValue} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            )}
            <TextField margin="normal" label="UID" value={user.uid || ''} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="500" sx={{ mt: 1 }}>Upload Summary</Typography>
            <Box sx={{ mt: 1.5, mb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Uploads</Typography>
                    <FileUploadOutlinedIcon color="primary" fontSize="small" />
                  </Box>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.2, mb: 0, minHeight: '1rem', display: 'flex', alignItems: 'center' }}>
                    {user.totalUploads !== undefined ? user.totalUploads : 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Total number of successful uploads.</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">Last Upload</Typography>
                    <CalendarMonthOutlinedIcon color="primary" fontSize="small" />
                  </Box>
                  <Typography variant="h6" component="div" sx={{ lineHeight: 1.2, mb: 0, whiteSpace: 'nowrap', minHeight: '1.5em' }}>
                    {formatTimestamp(user.lastUploadAt)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Most recent upload timestamp.</Typography>
                </CardContent>
              </Card>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="500">User Roles & Permissions</Typography>
            <FormControlLabel control={<Checkbox checked={!!user.isAdmin} disabled />} label="Administrator Access" />
            <FormControlLabel control={<Checkbox checked={!!user.isDemo} disabled />} label="Demo Mode" />
            <FormControlLabel control={<Checkbox checked={!!user.isOffline} disabled />} label="Offline Access" />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="500">Activity Metadata</Typography>
            <TextField margin="normal" label="Last sign-in time" value={formatTimestamp(user.metadata?.lastSignInTime)} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Last refresh time" value={formatTimestamp(user.metadata?.lastRefreshTime)} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Created by" value={user.createdBy || 'N/A'} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Created at" value={formatTimestamp(user.createdAt)} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Updated by" value={user.updatedBy || user.createdBy || 'N/A'} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
            <TextField margin="normal" label="Updated at" value={formatTimestamp(user.updatedAt)} fullWidth InputProps={{ readOnly: true, style: { color: 'rgba(0,0,0,0.87)', pointerEvents: 'none' } }} sx={disabledSx} />
          </>
        )}

        {/* ── MFA Tab ──────────────────────────────────────────────────────── */}
        {activeTab === 1 && (
          <>
            <Typography variant="subtitle1" fontWeight="500" sx={{ mt: 1 }}>Multi‑Factor Authentication</Typography>
            <FormControlLabel control={<Checkbox checked={!!user.emailVerified} disabled />} label="Email Verified" />
            <FormControlLabel control={<Checkbox checked={requiresMfa} disabled />} label="MFA Required" />
            <FormControlLabel control={<Checkbox checked={totpEnrolled} disabled />} label="TOTP Enrolled" />

            {totpEnrolled && !confirming && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" color="warning" onClick={() => { setConfirming(true); setResetError(''); }}>
                  Reset Multi‑Factor Setup
                </Button>
              </Box>
            )}
            {confirming && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Unenroll authenticator?</Typography>
                <Button color="warning" onClick={handleResetMfa} disabled={resetting} size="small">
                  {resetting ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
                </Button>
                <Button onClick={() => { setConfirming(false); setResetError(''); }} disabled={resetting} size="small">Cancel</Button>
                {resetError && <Typography variant="caption" color="error">{resetError}</Typography>}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="500">Trusted Devices</Typography>
              {devices.length > 0 && !revokeAllConfirming && (
                <Button size="small" color="error" onClick={() => setRevokeAllConfirming(true)}>
                  Revoke All
                </Button>
              )}
            </Box>

            {revokeAllConfirming && (
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Revoke all trusted devices?</Typography>
                <Button color="error" onClick={handleRevokeAll} disabled={revokingAll} size="small">
                  {revokingAll ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
                </Button>
                <Button onClick={() => setRevokeAllConfirming(false)} disabled={revokingAll} size="small">Cancel</Button>
              </Box>
            )}

            {devicesLoading && <CircularProgress size={20} />}
            {devicesError && <Typography color="error" variant="caption">{devicesError}</Typography>}

            {!devicesLoading && !devicesError && devices.length === 0 && (
              <Typography variant="body2" color="text.secondary">No trusted devices registered.</Typography>
            )}

            {!devicesLoading && devices.map((device) => (
              <Box key={device.deviceId} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight="500">{device.deviceLabel}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Added {formatTimestamp(device.createdAt)} · Last used {formatTimestamp(device.lastUsedAt)}
                    </Typography>
                  </Box>
                  {confirmingRevokeDeviceId !== device.deviceId && (
                    <Button size="small" color="error" disabled={!!revokingDeviceId} onClick={() => setConfirmingRevokeDeviceId(device.deviceId)}>
                      Revoke
                    </Button>
                  )}
                </Box>
                {confirmingRevokeDeviceId === device.deviceId && (
                  <Box sx={{ pb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">Revoke this device?</Typography>
                    <Button color="error" size="small" onClick={() => handleRevokeDevice(device.deviceId)} disabled={revokingDeviceId === device.deviceId}>
                      {revokingDeviceId === device.deviceId ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
                    </Button>
                    <Button size="small" onClick={() => setConfirmingRevokeDeviceId(null)} disabled={revokingDeviceId === device.deviceId}>Cancel</Button>
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
